// SINCE YOU LEFT and the DIGEST (docs/design/inbox.md §7, R1851; comps D
// and E of `.impeccable/mocks/inbox/`, round 089 cards 19 a to 23).
//
// SINCE YOU LEFT is one line under the filter row with ▾ at its end. ▾
// opens the block: the range row, the range line, the rows SIEGE, LOSSES,
// SICKBAY, WATER and WORKS, three figure cells with a spark, ALL FIGURES ›
// and the presses SHOW ONLY THESE and MARK THESE READ; ▴ folds it. ALL
// FIGURES › opens the DIGEST page: the charts and the ledger table of the
// chosen range, with ‹ BACK.
//
// The ledger rows are counts and the module sums them (`digest.ts`). A
// siege and a captain in the Sickbay are live states and not counts: the
// SIEGE row reads the live pledges of the Sieges frame, and the SICKBAY row
// reads the roster (audit P1). The module asks for the ledger on an event
// alone (ui-map-interface.md §10): the open of the INBOX, ▾, a range, ALL
// FIGURES and a return; the feed asks when the list changes while the
// INBOX stands open.
//
// The view stands in this module, because one page holds one INBOX. A
// press carries an `ib-s-` act, and `press.ts` hands it here.

import { hhmm } from '../../locale/clock';
import './sinceleft.css';
import { briefing as S } from '../../gen/strings';
import type { InboxLedgerState, InboxMsg } from '../../gen/proto/aquaphobia';
import { type InboxLedgerKey } from '../../gen/inboxledger';
import { groupThousands } from '../../locale/group';
import { factionName } from '../../world';
import { siegeWarnings, type SiegeWarning } from '../alarms';
import { arrowFace, arrowGlyph } from '../arrowpress';
import { esc } from '../esc';
import { sitePlace } from '../overlay/markup';
import { withMarkIcons } from '../resicon';
import {
  awayOf, awaySpan, changeText, chartsHtml, DASH, fig, figHtml, ledgerTableHtml, momentHead, momentsOf, type Range,
  type RangeId, RANGE_IDS, rangeLabel, rangeLine, rangeOf, rangeRowHtml, rangeStart, sparkSvg, worksOf,
} from './digest';
import type { InboxCtx } from './page';
import { onInboxPress } from './press';
import { bucketIcon, lineOneHtml, lineTwoHtml } from './rows';

// ---------------------------------------------------------------------
// The view

/** What the reader opened: the block, its range, the filter of the
 *  record and the DIGEST page. */
interface View {
  open: boolean;
  /** The range the reader picked, or null for the default. */
  range: RangeId | null;
  /** SHOW ONLY THESE stands: the record shows the messages of the range. */
  only: boolean;
  page: boolean;
  /** A GO TO of the DIGEST page moved the map: the next open of the INBOX
   *  comes back to the page and its range, once. */
  held: boolean;
}

const view: View = { open: false, range: null, only: false, page: false, held: false };

/** Folds the block and closes the page: the INBOX opens so. */
export function foldSinceLeft(): void {
  view.open = false;
  view.range = null;
  view.only = false;
  view.page = false;
  view.held = false;
}

/** The INBOX opened: the block stands folded, and the line asks for the
 *  figures it states. The one exception is the open that follows a GO TO
 *  of the DIGEST page: a telephone reader put the sheet down to see the
 *  map, and comes back to the page and its range. */
export function inboxOpened(c: InboxCtx): void {
  if (view.held) view.held = false;
  else foldSinceLeft();
  c.actions.inboxLedgerQuery();
}

/** A return: the INBOX opens with the block unfolded on SINCE YOU LEFT
 *  (R1605, R1851). */
export function unfoldSinceLeft(c: InboxCtx): void {
  foldSinceLeft();
  view.open = true;
  view.range = 'away';
  c.actions.inboxLedgerQuery();
}

/** Whether the DIGEST page stands in place of the INBOX page. */
export function digestShown(): boolean {
  return view.page;
}

/** The range the block reads: the pick of the reader, else SINCE YOU LEFT
 *  while the ledger holds a departure (or has not answered yet), else 24H. */
function chosen(l: InboxLedgerState | null): RangeId {
  const away = l === null || awayOf(l) > 0;
  if (view.range !== null && (view.range !== 'away' || away)) return view.range;
  return away ? 'away' : 'h24';
}

/** The chosen range of the ledger, summed, or null before the answer. */
function chosenRange(c: InboxCtx): { id: RangeId; r: Range | null } {
  const l = c.state.inboxLedger;
  const id = chosen(l);
  return { id, r: l === null ? null : rangeOf(l, id) };
}

/**
 * The filter of SHOW ONLY THESE: the messages filed at or after the start
 * of the chosen range, or null while no filter stands. The record applies
 * it to its list. The filter stands while the block stands open; a fold
 * drops it.
 */
export function sinceLeftKeep(c: InboxCtx): ((m: InboxMsg) => boolean) | null {
  if (!view.open || !view.only) return null;
  const { r } = chosenRange(c);
  if (r === null) return null;
  const from = rangeStart(r);
  return (m) => Number(m.unix) >= from;
}

// ---------------------------------------------------------------------
// The live states

/** What the block states of the present: the sieges against the reader
 *  now, the sieges of the return briefing that ended since, and the
 *  captains in the Sickbay with the world seconds until each is fit. */
export interface LiveStates {
  sieges: readonly SiegeWarning[];
  ended: readonly string[];
  sick: readonly { name: string; leftSec: number }[];
}

/** The live states of the store. A siege of the return briefing that has
 *  no live pledge any more has ended (audit P1); it shows on SINCE YOU
 *  LEFT alone, because the briefing is the record of the absence. */
export function liveStates(c: InboxCtx, id: RangeId): LiveStates {
  const s = c.state;
  const sieges = siegeWarnings(s.sieges, {
    faction: s.faction,
    span: s.rules.objWindowSec,
    windowIn: (hour) => s.windowInSec(hour),
    site: sitePlace,
    attacker: factionName,
  });
  const live = new Set(sieges.map((w) => w.siteId));
  const ended = id === 'away'
    ? (s.briefing?.sieges ?? []).filter((g) => !live.has(g.siteId)).map((g) => g.siteName)
    : [];
  const sick = s.roster
    .filter((cap) => cap.injuredSecs > 0 && !cap.capturedBy)
    .map((cap) => ({ name: cap.name, leftSec: cap.injuredSecs }))
    .sort((a, b) => a.leftSec - b.leftSec);
  return { sieges, ended, sick };
}

// ---------------------------------------------------------------------
// The rows of the block

/** One row of the block: a mark, the label, the detail and the figure.
 *  `detail` and `figure` are markup, escaped here. */
export interface BlockRow {
  key: 'siege' | 'losses' | 'sickbay' | 'water' | 'works';
  label: string;
  detail: string;
  figure: string;
  /** bad: the siege and the losses take the danger ink on the mark; dim:
   *  a siege that ended. The others stay neutral (comp D, not gold). */
  tone: 'bad' | 'plain' | 'dim';
}

/** A span in whole minutes, rounded up, so a countdown changes the markup
 *  once a minute and the panel does not draw again each second. */
export function minuteSpan(sec: number): string {
  const total = Math.max(0, Math.ceil(sec / 60));
  const h = Math.floor(total / 60), m = total % 60;
  return h > 0 ? S.left_hours({ hours: h, min: m }) : S.left_min({ min: m });
}

/** The end of a line that names fewer items than it counts. */
function more(text: string, extra: number): string {
  return extra > 0 ? `${text} · ${S.more({ n: extra })}` : text;
}

/** A sum of credits with its mark. */
function creditsHtml(v: number): string {
  return withMarkIcons(`¤${groupThousands(v)}`);
}

/** `+won −lost`, each part only when it is not zero. */
function plusMinus(plus: number, minus: number): string {
  const out: string[] = [];
  if (plus > 0) out.push(`<span class="pos">+${groupThousands(plus)}</span>`);
  if (minus > 0) out.push(`<span class="neg">−${groupThousands(minus)}</span>`);
  return out.join(' ');
}

/**
 * The rows of the block, in the order of need: SIEGE, LOSSES, SICKBAY,
 * WATER and WORKS. A row with nothing to say is left out. `r` is null
 * before the first answer; the live rows stand without it.
 */
export function blockRows(r: Range | null, live: LiveStates): BlockRow[] {
  const rows: BlockRow[] = [];
  const g = live.sieges[0];
  if (g !== undefined) {
    const left = esc(minuteSpan(g.leftSec));
    const detail = g.attacker
      ? S.siege_live({ site: esc(g.site), attacker: esc(g.attacker), left })
      : S.siege_live_bare({ site: esc(g.site), left });
    rows.push({ key: 'siege', label: S.lab_siege, detail: more(detail, live.sieges.length - 1),
      figure: esc(hhmm(g.closeSec)), tone: 'bad' });
  } else if (live.ended.length > 0) {
    rows.push({ key: 'siege', label: S.lab_siege,
      detail: more(S.siege_ended({ site: esc(live.ended[0]!) }), live.ended.length - 1),
      figure: esc(S.ended), tone: 'dim' });
  }
  const f = (k: InboxLedgerKey) => (r === null ? 0 : fig(r.sums, k));
  const lost = f('fleets_lost'), captured = f('captains_captured'), hurt = f('captains_hurt'), cargo = f('cargo_lost');
  if (lost + captured + hurt + cargo > 0) {
    const parts: string[] = [];
    if (lost > 0) parts.push(S.loss_fleets({ n: lost }));
    if (captured > 0) parts.push(S.loss_captured({ n: groupThousands(captured) }));
    if (hurt > 0) parts.push(S.loss_hurt({ n: groupThousands(hurt) }));
    if (cargo > 0) parts.push(S.loss_cargo({ n: groupThousands(cargo) }));
    rows.push({ key: 'losses', label: S.lab_losses, detail: esc(parts.join(' · ')),
      figure: figHtml(lost), tone: 'bad' });
  }
  const sick = live.sick[0];
  if (sick !== undefined) {
    rows.push({ key: 'sickbay', label: S.lab_sickbay,
      detail: more(S.sickbay_fit({ name: esc(sick.name), left: esc(minuteSpan(sick.leftSec)) }), live.sick.length - 1),
      figure: groupThousands(live.sick.length), tone: 'plain' });
  }
  const won = f('cells_won'), cl = f('cells_lost');
  const taken = f('obj_taken'), held = f('obj_held'), ol = f('obj_lost');
  if (won + cl + taken + held + ol > 0) {
    const parts: string[] = [];
    if (won + cl > 0) parts.push(S.water_cells({ won: groupThousands(won), lost: groupThousands(cl) }));
    const objs: string[] = [];
    if (taken > 0) objs.push(S.obj_taken({ n: groupThousands(taken) }));
    if (held > 0) objs.push(S.obj_held({ n: groupThousands(held) }));
    if (ol > 0) objs.push(S.obj_lost({ n: groupThousands(ol) }));
    if (objs.length > 0) parts.push(S.water_objects({ list: objs.join(', ') }));
    rows.push({ key: 'water', label: S.lab_water, detail: esc(parts.join(' · ')),
      figure: plusMinus(won, cl) || DASH, tone: 'plain' });
  }
  const works = r === null ? 0 : worksOf(r.sums);
  if (works > 0) {
    const parts: string[] = [];
    const add = (n: number, word: (p: { n: number }) => string) => { if (n > 0) parts.push(word({ n })); };
    add(f('jobs_done'), S.works_levels);
    add(f('research_done'), S.works_research);
    add(f('ships_ready'), S.works_hulls);
    add(f('repairs'), S.works_repairs);
    add(f('torps_stowed'), S.works_torps);
    add(f('captains_recruited'), S.works_recruits);
    rows.push({ key: 'works', label: S.lab_works, detail: esc(parts.join(' · ')),
      figure: groupThousands(works), tone: 'plain' });
  }
  return rows;
}

function rowHtml(row: BlockRow): string {
  return `<div class="sl-row ${row.tone}" data-k="${row.key}"><i class="sl-mk"></i>`
    + `<b class="sl-lab">${esc(row.label)}</b><span class="sl-det">${row.detail}</span>`
    + `<span class="sl-fig">${row.figure}</span></div>`;
}

/** The three figure cells: the label, the figure, the change against the
 *  range before (24H and 7D), and a spark over the rows of the range. */
const CELLS: readonly { key: InboxLedgerKey; label: string; ink: string; credits?: boolean }[] = [
  { key: 'kills', label: S.cell_sinkings, ink: 'sp-text' },
  { key: 'isotopes', label: S.cell_isotopes, ink: 'sp-iso' },
  { key: 'credits', label: S.cell_credits, ink: 'sp-text', credits: true },
];

function cellsHtml(r: Range | null): string {
  return CELLS.map((cell) => {
    const v = r === null ? 0 : fig(r.sums, cell.key);
    const values = r === null ? [] : r.rows.map((row) => fig(row, cell.key));
    const shown = v === 0 ? DASH : cell.credits ? creditsHtml(v) : groupThousands(v);
    const change = r?.prev ? `<em>${esc(changeText(v, fig(r.prev, cell.key)))}</em>` : '';
    return `<div class="sl-fc" data-k="${cell.key}">${sparkSvg(values, cell.ink)}`
      + `<span class="sl-ft"><i>${esc(cell.label)}</i><b>${shown}</b>${change}</span></div>`;
  }).join('');
}

// ---------------------------------------------------------------------
// The folded line and the open block

/** The parts of the folded line: the siege now, then each figure of the
 *  range that is not zero (comp E). */
function lineParts(r: Range | null, live: LiveStates): string[] {
  const parts: string[] = [];
  const g = live.sieges[0];
  if (g !== undefined) {
    parts.push(`<span class="neg">${esc(S.line_siege({ site: g.site, at: hhmm(g.closeSec) }))}</span>`);
  }
  if (r === null) return parts;
  const lost = fig(r.sums, 'fleets_lost');
  if (lost > 0) parts.push(esc(S.line_lost({ n: groupThousands(lost) })));
  const won = fig(r.sums, 'cells_won'), cl = fig(r.sums, 'cells_lost');
  if (won + cl > 0) parts.push(`${esc(S.line_cells)} ${plusMinus(won, cl)}`);
  const credits = fig(r.sums, 'credits');
  if (credits > 0) parts.push(creditsHtml(credits));
  const works = worksOf(r.sums);
  if (works > 0) parts.push(esc(S.line_works({ n: works })));
  return parts;
}

function lineHtml(c: InboxCtx, id: RangeId, r: Range | null): string {
  const live = liveStates(c, id);
  const parts = lineParts(r, live);
  // A range with no figure but an unread message states the count, so the
  // line never reads "nothing new" over an unread item.
  const unread = parts.length > 0 ? 0 : unreadOf(c, r).length;
  const sum = parts.length > 0 ? parts.join(' <span class="sl-dot">·</span> ')
    : esc(r === null ? S.line_wait : unread > 0 ? S.line_unread({ n: unread }) : S.line_quiet);
  const away = r?.id === 'away' ? `<span class="sl-away">${esc(awaySpan(r))}</span>` : '';
  return `<div class="sl sl-folded"><button class="sl-line" data-act="ib-s-open" aria-expanded="false"`
    + ` title="${esc(S.fold_open)}"><b class="sl-name">${esc(rangeLabel(id))}</b>${away}`
    + `<span class="sl-sum">${sum}</span><span class="sl-chev" aria-hidden="true">${arrowFace('down')}</span></button></div>`;
}

/** The ids of the unread messages of the range. A read the server has not
 *  answered yet counts as read, as in the record (inbox.md §6, R2242). */
function unreadOf(c: InboxCtx, r: Range | null): number[] {
  if (r === null) return [];
  const from = rangeStart(r);
  return c.state.inbox.filter((m) => !c.state.inboxIsRead(m) && Number(m.unix) >= from).map((m) => m.id);
}

/** The range row of the block and of the page: SINCE YOU LEFT takes no
 *  press while the ledger holds no departure. */
function rangeRow(c: InboxCtx, id: RangeId): string {
  const l = c.state.inboxLedger;
  return rangeRowHtml(id, l === null || awayOf(l) > 0);
}

/** The range line under the range row, or the wait before the answer. */
function spanHtml(r: Range | null): string {
  return `<div class="sl-span">${esc(r === null ? S.line_wait : rangeLine(r))}</div>`;
}

function blockHtml(c: InboxCtx, id: RangeId, r: Range | null): string {
  const rows = blockRows(r, liveStates(c, id));
  const unread = unreadOf(c, r);
  return '<div class="sl sl-open">'
    + `<div class="sl-rr">${rangeRow(c, id)}`
    + `<button class="sl-fold sqp" data-act="ib-s-fold" aria-expanded="true" title="${esc(S.fold_close)}"`
    + ` aria-label="${esc(S.fold_close)}">${arrowGlyph('up')}</button></div>`
    + spanHtml(r)
    + `<div class="sl-rows">${rows.length > 0 ? rows.map(rowHtml).join('') : `<div class="sl-quiet">${esc(S.quiet)}</div>`}</div>`
    + `<div class="sl-figs">${cellsHtml(r)}</div>`
    + `<button class="sl-all" data-act="ib-s-all"><span>${esc(S.all_figures)}</span><i aria-hidden="true">›</i></button>`
    + '<div class="sl-acts">'
    + `<button class="sl-b${view.only ? ' on' : ''}" data-act="ib-s-only" aria-pressed="${view.only}">`
    + `${esc(view.only ? S.show_every : S.show_only)}</button>`
    + `<button class="sl-b" data-act="ib-s-read"${unread.length === 0 ? ' disabled' : ''}>${esc(S.mark_these)}</button>`
    + '</div></div>';
}

/** SINCE YOU LEFT: the folded line, or the open block. */
export function sinceLeftHtml(c: InboxCtx): string {
  const { id, r } = chosenRange(c);
  return view.open ? blockHtml(c, id, r) : lineHtml(c, id, r);
}

// ---------------------------------------------------------------------
// The DIGEST page

/** The top moments of the range, from the messages the INBOX holds. */
function momentsHtml(c: InboxCtx, r: Range): string {
  const list = momentsOf(c.state.inbox, rangeStart(r));
  const body = list.length === 0 ? `<div class="sl-quiet">${esc(S.m_none)}</div>`
    : list.map(({ key, msg }) => {
      const verb = msg.replay ? S.m_replay : msg.cell ? S.m_goto : '';
      return `<button class="dg-mo" data-act="ib-s-moment" data-id="${msg.id}">`
        + `<span class="dg-mh"><b>${esc(momentHead(key))}</b>${verb ? `<i>${esc(verb)}</i>` : ''}</span>`
        + `<span class="dg-mr">${lineOneHtml(msg, c.state)}${lineTwoHtml(msg, c.state)}</span></button>`;
    }).join('');
  return `<section class="dg-cb"><h4 class="dg-sh"><b>${esc(S.ch_moments)}</b><i>${esc(S.ch_moments_aside)}</i></h4>${body}</section>`;
}

/** The DIGEST page: ‹ BACK, the pinned range row and range line, the
 *  charts of card 20 a, the top moments and the ledger table of card
 *  21 a. */
export function digestHtml(c: InboxCtx): string {
  const l = c.state.inboxLedger;
  const { id, r } = chosenRange(c);
  const ranges: Partial<Record<RangeId, Range>> = {};
  if (l !== null) {
    for (const k of RANGE_IDS) {
      const x = rangeOf(l, k);
      if (x !== null) ranges[k] = x;
    }
  }
  const head = `<div class="dg-bar"><button class="dg-back" data-act="ib-s-back">${esc(S.back)}</button>`
    + `<b class="dg-h">${esc(S.page_head)}</b></div>`;
  const pin = `<div class="dg-pin"><div class="sl-rr">${rangeRow(c, id)}</div>${spanHtml(r)}</div>`;
  if (r === null) return `<div class="dg">${head}${pin}</div>`;
  return `<div class="dg">${head}${pin}${chartsHtml(r)}${momentsHtml(c, r)}`
    + `<section class="dg-cb"><h4 class="dg-sh"><b>${esc(S.ch_ledger)}</b><i>${esc(S.ledger_aside)}</i></h4>`
    + `${ledgerTableHtml(ranges, id, (group) => bucketIcon(group, 12))}</section></div>`;
}

// ---------------------------------------------------------------------
// The presses

/** One press of the block or of the page. The overlay draws the panel
 *  again after it. */
function pressSinceLeft(act: string, el: HTMLElement, c: InboxCtx): void {
  const a = c.actions;
  view.held = false;
  switch (act) {
    case 'ib-s-open':
      view.open = true;
      a.inboxLedgerQuery();
      return;
    case 'ib-s-fold':
      foldSinceLeft();
      return;
    case 'ib-s-range': {
      const id = el.dataset.id as RangeId | undefined;
      if (id === undefined || !RANGE_IDS.includes(id)) return;
      view.range = id;
      a.inboxLedgerQuery();
      return;
    }
    case 'ib-s-all':
      view.page = true;
      a.inboxLedgerQuery();
      return;
    case 'ib-s-back':
      view.page = false;
      return;
    case 'ib-s-only':
      view.only = !view.only;
      return;
    case 'ib-s-read': {
      // A press with the link down sends nothing: the player hears so, and
      // the block stays open with every message unread (audit P2).
      if (!a.linkUp()) {
        a.refuse(S.link_down);
        return;
      }
      // One action carries the set, and the world answers with one list
      // (R1851); an action a message would cost a list a message.
      const ids = unreadOf(c, chosenRange(c).r);
      if (ids.length > 0) {
        a.inboxReadMany(ids);
        // The counts and the rows read the reads at once (audit P5).
        c.state.markInboxRead(ids);
      }
      foldSinceLeft();
      return;
    }
    case 'ib-s-moment': {
      const m = c.state.inbox.find((x) => x.id === Number(el.dataset.id));
      if (!m) return;
      if (m.replay) {
        if (!a.linkUp()) {
          a.refuse(S.link_down);
          return;
        }
        a.openReplay(m.replay);
      } else if (m.cell) {
        a.focusCell(m.cell);
        c.collapse?.();
        view.held = true;
      }
      return;
    }
  }
}

onInboxPress('ib-s-', pressSinceLeft);
